import { useMemo, useState } from 'react';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { ApprovalOperation, ApprovalRules, ApprovalStatus } from 'ui-product-approvals-common';
import { parseActor, serializeActor } from 'ui-product-approvals-common/utils/actorKey';
import { convertToTimestamp, getCurrentMinutes, getDefaultTimeForDate, getDueDateDaysAway, getDueDateFromOffset, getMinTimeForToday } from '../../utils/dueDate';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { NOTE_SANITIZE_CONFIG } from '../../constants/sanitize';
import { getErrorSubCategoryFromError } from '../../utils/errorUtils';
import { floatingErrorAlert, operationSuccessAlert } from '../../utils/alertUtils';
import { getIsApprovalTitleError, getPrimaryButtonDisabledState } from '../../utils/approvalModalUtils';
import { getAvailableApprovalRules } from '../../utils/approvalRulesUtils';
import { countOrderedSteps, getApproverTrackingCounts, toApprovalStepInputs } from '../../utils/approvalStepsUtils';
import { validateApprovalProcess } from '../../utils/approvalProcessValidation';
import { getInitialStepGroups, getLockedTemplateApproverActorKeys, getLockedTemplateStageIndexes, getUnavailableTemplateApproverActorKeys } from '../../utils/approvalTemplateUtils';
import { trackInteraction } from '../../utils/usageTracker';
import { APPROVAL_MODAL_CHANGED_APPROVER, APPROVAL_MODAL_DEFAULT_AGENT_REMOVED, APPROVAL_MODAL_FORM_SUBMITTED, APPROVAL_MODAL_TITLE_CHANGED, APPROVAL_REQUEST_CHANGES, APPROVAL_RULES_ALL_APPROVERS, APPROVAL_RULES_ANY_APPROVER } from '../../constants/trackingConstants';
import { ACTOR, DEFAULT_AGENT_APPROVER_ID } from '../../constants/actors';
import { GATES } from '../../constants/auth';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { useApprovalConditions } from '../../hooks/useApprovalConditions';
import { useApprovalSteps } from '../../hooks/useApprovalSteps';
import { useFetchUsersByScope } from '../../hooks/useFetchUsersByScope';
import { useGetIsUngated } from '../../hooks/useGetIsUngated';
import { useHasOrderedApprovalSteps } from '../../hooks/useHasOrderedApprovalSteps';
import { useHasSeqReqChangesRedesign } from '../../hooks/useHasSeqReqChangesRedesign';
import { ApprovalModalBody } from './ApprovalModalBody';
import { ApprovalModalFooter } from './ApprovalModalFooter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getInitialApprovalRule = (template, availableApprovalRules) => {
  var _template$approvalRul;
  const templateRule = template === null || template === void 0 || (_template$approvalRul = template.approvalRule) === null || _template$approvalRul === void 0 ? void 0 : _template$approvalRul.value;
  return Object.values(ApprovalRules).includes(templateRule) ? templateRule : availableApprovalRules[0];
};
const getInteractionName = (interaction, approvalOperation) => `${interaction}-${approvalOperation}`;
export const ApprovalModalForm = ({
  alertContent,
  approvers,
  buttonUse,
  currentOperation,
  onCancel,
  onClose,
  onRequestSuccess: onRequestSuccessCallback,
  showFeedbackSurvey,
  template,
  resolvedTemplateApprovers
}) => {
  var _template$approvalRul2, _template$approvalRul3, _template$approvalSte, _template$requesterNo3, _template$requesterNo4;
  const approval = useApprovalContext();
  const {
    approvalType,
    assetName,
    currentUser
  } = approval.meta;
  const {
    userCanSkipThisApproval
  } = useApprovalConditions();
  const isRequestOperation = currentOperation === ApprovalOperation.REQUEST;
  const getIsUngated = useGetIsUngated();
  const canEditApprovalTitle = getIsUngated(GATES.MARKETING_STUDIO_HOME);
  const hasAgentGate = getIsUngated(GATES.AGENT_APPROVER);
  const isOrderedApprovalSteps = useHasOrderedApprovalSteps(approvalType);
  const isProcessRedesign = useHasSeqReqChangesRedesign(approvalType);
  const {
    users: superAdminUsers
  } = useFetchUsersByScope('super-admin', {
    skip: !isOrderedApprovalSteps
  });
  const superAdminUserIds = useMemo(() => new Set(superAdminUsers.map(user => String(user.id))), [superAdminUsers]);
  const availableApprovalRules = getAvailableApprovalRules({
    approvalType
  });
  const approvalRuleLocked = (_template$approvalRul2 = template === null || template === void 0 || (_template$approvalRul3 = template.approvalRule) === null || _template$approvalRul3 === void 0 ? void 0 : _template$approvalRul3.locked) !== null && _template$approvalRul2 !== void 0 ? _template$approvalRul2 : false;
  const approvalStepListLocked = (_template$approvalSte = template === null || template === void 0 ? void 0 : template.approvalStepListLocked) !== null && _template$approvalSte !== void 0 ? _template$approvalSte : false;
  const lockedApproverActorKeys = useMemo(() => getLockedTemplateApproverActorKeys(template), [template]);
  const lockedTemplateStageIndexes = useMemo(() => getLockedTemplateStageIndexes(template, resolvedTemplateApprovers), [resolvedTemplateApprovers, template]);
  const unavailableTemplateApproverActorKeys = useMemo(() => getUnavailableTemplateApproverActorKeys(template, approvers), [approvers, template]);
  const defaultAgentApprover = useMemo(() => isRequestOperation && hasAgentGate && !template && !approvalStepListLocked && !lockedTemplateStageIndexes.has(0) ? approvers.find(approver => approver.actor.actorType === ACTOR.AGENT && approver.actor.id === DEFAULT_AGENT_APPROVER_ID) : undefined, [approvers, hasAgentGate, isRequestOperation, template, approvalStepListLocked, lockedTemplateStageIndexes]);
  const initialStepGroups = useMemo(() => getInitialStepGroups(template, resolvedTemplateApprovers, defaultAgentApprover), [defaultAgentApprover, resolvedTemplateApprovers, template]);
  const [approvalRule, setApprovalRule] = useState(() => getInitialApprovalRule(template, availableApprovalRules));
  const approvalSteps = useApprovalSteps(initialStepGroups, lockedApproverActorKeys, lockedTemplateStageIndexes, approvalStepListLocked, approvalRule);
  const [approvalTitle, setApprovalTitle] = useState(assetName);
  const approvalTitleError = getIsApprovalTitleError(approvalTitle);
  const [requestLoading, setRequestLoading] = useState(false);
  const [note, setNote] = useState(() => {
    var _template$requesterNo, _template$requesterNo2;
    return (_template$requesterNo = template === null || template === void 0 || (_template$requesterNo2 = template.requesterNote) === null || _template$requesterNo2 === void 0 ? void 0 : _template$requesterNo2.value) !== null && _template$requesterNo !== void 0 ? _template$requesterNo : '';
  });
  const [isNoteOverLimit, setIsNoteOverLimit] = useState(false);
  const [approverActors, setApproverActors] = useState(() => {
    var _template$approvalSte2;
    return (_template$approvalSte2 = template === null || template === void 0 ? void 0 : template.approvalSteps.map(step => step.actor)) !== null && _template$approvalSte2 !== void 0 ? _template$approvalSte2 : [];
  });
  const [initialDueDate] = useState(() => template !== null && template !== void 0 && template.dueDateRule ? getDueDateFromOffset(template.dueDateRule.offset) : undefined);
  const [dueDate, setDueDate] = useState(initialDueDate);
  const [dueTime, setDueTime] = useState(() => {
    if (template !== null && template !== void 0 && template.dueDateRule) {
      return template.dueDateRule.offset === 0 ? getMinTimeForToday() : getCurrentMinutes();
    }
    return getDefaultTimeForDate(initialDueDate !== null && initialDueDate !== void 0 ? initialDueDate : null);
  });
  const [isDueDateValid, setIsDueDateValid] = useState(true);
  const orderedValidation = useMemo(() => validateApprovalProcess(approvalSteps.steps), [approvalSteps.steps]);
  const hasUnavailableTemplateApprover = useMemo(() => approvalSteps.steps.some(step => step.approvers.some(approver => unavailableTemplateApproverActorKeys.has(serializeActor(approver.actor)))), [approvalSteps.steps, unavailableTemplateApproverActorKeys]);
  const selectedApproversCount = isOrderedApprovalSteps ? orderedValidation.distinctApproverCount : approverActors.length;
  const hasEmptyApprovalStep = isOrderedApprovalSteps && orderedValidation.hasEmptyStep;
  const isMissingHumanApprover = isOrderedApprovalSteps && !hasEmptyApprovalStep && !orderedValidation.hasHumanApprover;
  const trackModalInteraction = (interaction, extra) => {
    trackInteraction(getInteractionName(interaction, currentOperation), approvalType, extra);
  };
  const onChangeApprovers = value => {
    const {
      userCount,
      teamCount,
      agentIds
    } = getApproverTrackingCounts(value);
    trackModalInteraction(APPROVAL_MODAL_CHANGED_APPROVER, {
      userCount,
      teamCount,
      agentId: agentIds
    });
    setApproverActors(value);
  };
  const getFormSubmissionTrackingExtra = data => {
    const extra = {};
    if (data && 'createdApprovals' in data) {
      extra.approvalId = data.createdApprovals.map(createdApproval => createdApproval.id);
    } else {
      extra.approvalId = data.id;
    }
    if (currentOperation === ApprovalOperation.REQUEST) {
      extra.count = selectedApproversCount;
      extra.isSkipped = userCanSkipThisApproval;
      if (dueDate) {
        extra.dueDateDelta = getDueDateDaysAway(dueDate);
      }
      const submittedActors = isOrderedApprovalSteps ? toApprovalStepInputs(approvalSteps.steps).map(input => input.actor) : approverActors;
      extra.totalSteps = isOrderedApprovalSteps ? countOrderedSteps(approvalSteps.steps) : 1;
      const {
        userCount,
        teamCount,
        agentIds
      } = getApproverTrackingCounts(submittedActors);
      extra.userCount = userCount;
      extra.teamCount = teamCount;
      extra.agentId = agentIds;
    }
    if (currentOperation === ApprovalOperation.APPROVE && data && 'approvalStatus' in data && data.approvalStatus === ApprovalStatus.APPROVED) {
      extra.complete = true;
    }
    if (approvalRule === ApprovalRules.ANY_CAN_APPROVE) {
      extra.rules = APPROVAL_RULES_ANY_APPROVER;
    } else if (approvalRule === ApprovalRules.NON_SEQUENTIAL) {
      extra.rules = APPROVAL_RULES_ALL_APPROVERS;
    }
    return extra;
  };
  const onFormRequestSuccess = data => {
    const extra = getFormSubmissionTrackingExtra(data);
    trackModalInteraction(APPROVAL_MODAL_FORM_SUBMITTED, extra);
    // The redesign routes request-changes through this modal, so the dedicated
    // event (emitted on click by the gated-off LegacyRequestChangesCommentButton)
    // must fire here too or it zeroes out once the redesign ships.
    if (currentOperation === ApprovalOperation.REQUEST_CHANGES) {
      trackInteraction(APPROVAL_REQUEST_CHANGES, approvalType, {
        approvalId: extra.approvalId
      });
    }
    if (canEditApprovalTitle && approvalTitle !== assetName) {
      trackModalInteraction(APPROVAL_MODAL_TITLE_CHANGED, extra);
    }
    operationSuccessAlert(approvalType, currentOperation, assetName);
    onClose();
    setRequestLoading(false);
    onRequestSuccessCallback === null || onRequestSuccessCallback === void 0 || onRequestSuccessCallback(currentOperation);
  };
  const onRequestError = errorResponse => {
    setRequestLoading(false);
    floatingErrorAlert(approvalType, getErrorSubCategoryFromError(errorResponse));
    onClose();
  };
  const onSubmitForm = event => {
    event.preventDefault();
    setRequestLoading(true);
    const sanitizedNote = sanitize(note, NOTE_SANITIZE_CONFIG);
    if (currentOperation === ApprovalOperation.REQUEST) {
      const dueDateTimestamp = dueDate ? convertToTimestamp(dueDate, dueTime) : undefined;
      approval.api.create(sanitizedNote, isOrderedApprovalSteps ? toApprovalStepInputs(approvalSteps.steps) : approverActors.map(actor => ({
        actor
      })), approvalRule, dueDateTimestamp, canEditApprovalTitle ? approvalTitle : undefined).then(onFormRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.CANCEL || currentOperation === ApprovalOperation.RECALL) {
      approval.api.cancel(sanitizedNote).then(onFormRequestSuccess).catch(onRequestError);
    }
    // In the redesign, primary actions are taken from a
    // specific approver row, so a step id is set and we dispatch to the per-step
    // endpoints. In the ungated flow no step id is set and we keep the existing
    // approval-level behavior.
    const currentStepId = approval.meta.currentStepId;
    if (currentOperation === ApprovalOperation.APPROVE) {
      const request = currentStepId != null ? approval.api.approveStep(currentStepId, sanitizedNote) : approval.api.approve(sanitizedNote);
      request.then(onFormRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.REJECT) {
      const request = currentStepId != null ? approval.api.rejectStep(currentStepId, sanitizedNote) : approval.api.reject(sanitizedNote);
      request.then(onFormRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.REQUEST_CHANGES) {
      const request = currentStepId != null ? approval.api.requestChangesStep(currentStepId, sanitizedNote) : approval.api.requestChanges(sanitizedNote);
      request.then(onFormRequestSuccess).catch(onRequestError);
    }
  };
  const {
    isDisabled: disablePrimaryButton,
    disabledTooltip
  } = getPrimaryButtonDisabledState({
    approvalOperation: currentOperation,
    noteContent: note,
    isNoteTooLong: isNoteOverLimit,
    approversCount: selectedApproversCount,
    isDueDateValid,
    canEditApprovalTitle,
    approvalTitle,
    isApprovalTitleError: approvalTitleError,
    hasEmptyApprovalStep,
    isMissingHumanApprover,
    hasUnavailableTemplateApprover
  });
  const handleRemoveApprover = (stepId, actorKey) => {
    if (!template && hasAgentGate && parseActor(actorKey).id === DEFAULT_AGENT_APPROVER_ID) {
      trackInteraction(APPROVAL_MODAL_DEFAULT_AGENT_REMOVED, approvalType);
    }
    approvalSteps.removeApprover(stepId, actorKey);
  };

  // In the create flow the current user is the requester, who may not be
  // added as an approver.
  const requesterActorKey = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.user_id) === undefined ? undefined : serializeActor({
    actorType: ACTOR.USER,
    id: String(currentUser.user_id)
  });
  return /*#__PURE__*/_jsxs(Form, {
    onSubmit: onSubmitForm,
    children: [requestLoading ? /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(TFlex, {
        alignItems: "center",
        justifyContent: "center",
        padding: "300",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {})
      })
    }) : /*#__PURE__*/_jsx(ApprovalModalBody, {
      alertContent: alertContent,
      approvalRule: approvalRule,
      approvalSteps: approvalSteps,
      approvalTitle: approvalTitle,
      approvalType: approvalType,
      approverActors: approverActors,
      approvers: approvers,
      availableApprovalRules: availableApprovalRules,
      canEditApprovalTitle: canEditApprovalTitle,
      currentOperation: currentOperation,
      requesterActorKey: requesterActorKey,
      dueDate: dueDate,
      dueTime: dueTime,
      initialNote: (_template$requesterNo3 = template === null || template === void 0 || (_template$requesterNo4 = template.requesterNote) === null || _template$requesterNo4 === void 0 ? void 0 : _template$requesterNo4.value) !== null && _template$requesterNo3 !== void 0 ? _template$requesterNo3 : '',
      isDueDateValid: isDueDateValid,
      isNoteTooLong: isNoteOverLimit,
      isOrderedApprovalSteps: isOrderedApprovalSteps,
      isProcessRedesign: isProcessRedesign,
      onChangeApprovalRule: setApprovalRule,
      onChangeApprovalTitle: setApprovalTitle,
      onChangeApprovers: onChangeApprovers,
      onChangeNote: setNote,
      onNoteOverLimit: setIsNoteOverLimit,
      onRemoveApprover: handleRemoveApprover,
      setDueDate: setDueDate,
      setDueTime: setDueTime,
      setIsDueDateValid: setIsDueDateValid,
      superAdminUserIds: superAdminUserIds,
      approvalRuleLocked: approvalRuleLocked,
      lockedApproverActorKeys: lockedApproverActorKeys,
      unavailableTemplateApproverActorKeys: unavailableTemplateApproverActorKeys,
      approvalStepListLocked: approvalStepListLocked
    }), /*#__PURE__*/_jsx(ApprovalModalFooter, {
      approvalType: approvalType,
      approvalOperation: currentOperation,
      buttonUse: buttonUse,
      disablePrimaryButton: disablePrimaryButton,
      disabledTooltip: disabledTooltip,
      isLoading: requestLoading,
      onCancel: onCancel,
      showFeedbackSurvey: showFeedbackSurvey
    })]
  });
};